import { createContext, useContext, useState, ReactNode } from 'react'

export type Locale = 'es' | 'en'

interface I18nContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
  get: (key: string) => unknown
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

interface I18nProviderProps {
  children: ReactNode
  translations: Record<Locale, Record<string, any>>
}

export function I18nProvider({ children, translations }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    // Get from localStorage or default to browser language
    const stored = localStorage.getItem('nucelar-locale') as Locale | null
    if (stored && (stored === 'es' || stored === 'en')) {
      return stored
    }
    // Detect browser language
    const browserLang = navigator.language.split('-')[0]
    return browserLang === 'es' ? 'es' : 'en'
  })

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale)
    localStorage.setItem('nucelar-locale', newLocale)
  }

  const t = (key: string): string => {
    const localeTranslations = translations[locale]
    if (!localeTranslations) return key
    
    // Handle nested keys like "footer.links.home"
    const keys = key.split('.')
    let value: any = localeTranslations
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        return key // Return key if not found
      }
    }
    
    return typeof value === 'string' ? value : key
  }

  const get = (key: string): unknown => {
    const localeTranslations = translations[locale]
    if (!localeTranslations) return undefined
    const keys = key.split('.')
    let value: unknown = localeTranslations
    for (const k of keys) {
      if (value && typeof value === 'object' && k in (value as Record<string, unknown>)) {
        value = (value as Record<string, unknown>)[k]
      } else {
        return undefined
      }
    }
    return value
  }

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, get }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}
